import type { MetricCardState, MetricTrend } from "@components/display/MetricCard";
import { MetricCard } from "@components/display/MetricCard";
import { MetricsGrid } from "@components/display/MetricsGrid";
import { AlertTriangle, Bug, CheckCircle2, MessageSquare } from "lucide-react";
import { useTicketsMetrics } from "../../hooks/useTicketsMetrics";

function toMetricState(
  isLoading: boolean | undefined,
  isError: boolean | undefined,
  value: number,
  trend?: MetricTrend,
): MetricCardState {
  if (isLoading) return { type: "loading" };
  if (isError) return { type: "error" };
  return { type: "ready", value, trend };
}

/** Maps a signed delta (tickets last 7d vs previous 7d) to a MetricTrend badge. */
function toTrend(delta: number): MetricTrend {
  if (delta === 0) {
    return { value: 0, direction: "flat", tone: "neutral", label: "\u00b10" };
  }
  if (delta > 0) {
    return { value: delta, direction: "up", tone: "positive", label: `+${delta}` };
  }
  return {
    value: Math.abs(delta),
    direction: "down",
    tone: "negative",
    label: `-${Math.abs(delta)}`,
  };
}

interface TicketsMetricsCardsProps {
  isLoading?: boolean;
  isError?: boolean;
  data?: {
    total: number;
    byStatus: Record<string, number>;
    trends: {
      total: number;
      open: number;
      proposition_solution: number;
      resolved: number;
    };
  };
}

/**
 * KPI header of the Tickets tab, built on the shared MetricCard brick.
 * Mirrors the mockup stat-card row: Total / Ouverts / Propositions / Résolus.
 * Accents: primary = total headline, attention = open (pending action),
 * info = solution proposed, positive = resolved.
 */
export function TicketsMetricsCards({ isLoading, isError, data }: TicketsMetricsCardsProps) {
  return (
    <MetricsGrid columns={{ sm: 1, md: 4 }} className="md:gap-6">
      <MetricCard
        label="Total tickets"
        icon={Bug}
        accent="primary"
        state={toMetricState(
          isLoading,
          isError,
          data?.total ?? 0,
          toTrend(data?.trends?.total ?? 0),
        )}
      />
      <MetricCard
        label="Ouverts"
        icon={AlertTriangle}
        accent="attention"
        state={toMetricState(
          isLoading,
          isError,
          data?.byStatus?.open ?? 0,
          toTrend(data?.trends?.open ?? 0),
        )}
      />
      <MetricCard
        label="Propositions"
        icon={MessageSquare}
        accent="info"
        state={toMetricState(
          isLoading,
          isError,
          data?.byStatus?.proposition_solution ?? 0,
          toTrend(data?.trends?.proposition_solution ?? 0),
        )}
      />
      <MetricCard
        label="Résolus"
        icon={CheckCircle2}
        accent="positive"
        state={toMetricState(
          isLoading,
          isError,
          data?.byStatus?.resolved ?? 0,
          toTrend(data?.trends?.resolved ?? 0),
        )}
      />
    </MetricsGrid>
  );
}

/** Auto-fetching variant — wraps the hook and delegates to the presentational component. */
export function TicketsMetricsCardsWithQuery() {
  const { data, isLoading, isError } = useTicketsMetrics();
  return <TicketsMetricsCards isLoading={isLoading} isError={isError} data={data} />;
}
